import { useId, useState } from 'react';
import { createFileRoute, useNavigate } from '@tanstack/react-router';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { ArrowRight } from 'lucide-react';
import {
  Button,
  Card,
  Combobox,
  Input,
  Select,
  dayValue,
  hourValue,
  momentOf,
} from '@wundo/ui';
import {
  PlannedMeetingsPreview,
  type PlannedMeeting,
} from '@/components/onboarding/PlannedMeetingsPreview.tsx';
import { defaultStart } from '@/components/meetings/manualMeetingTime.ts';
import {
  defaultMeetingTemplateId,
  meetingTypeIsAChoice,
  meetingTypeOptions,
} from '@/components/meetings/meetingTypeChoice.ts';
import { fetchCurrentUser } from '@wundo/client/auth.ts';
import { createManualMeeting } from '@/lib/meetings.ts';
import { fetchMeetingTemplates } from '@wundo/client/memberMeetingTemplates.ts';
import { m } from '@/paraglide/messages.js';
import type { MeetingTemplateItem } from '@wundo/shared/meetingTemplate.ts';

/**
 * The last step for somebody without a calendar: one conversation, written down
 * by hand.
 *
 * It used to be a list of rows anybody could walk past empty, and people did —
 * which left them on an agenda with nothing on it, looking at the product they
 * had just set up doing nothing. So it asks for exactly one conversation and
 * will not move on without it, and the way out is that conversation's own page
 * rather than the agenda: the first thing somebody sees of Wundo is then their
 * own meeting being prepared, which is the whole promise of the two screens
 * they have just read.
 *
 * The same questions the agenda's dialog asks — a title, who it is with, a day,
 * an hour — and the same endpoint writes it, so a conversation typed here is in
 * no way a different kind of thing from one typed there. Who it is with is
 * always asked here, because this step exists only for somebody whose calendar
 * Wundo cannot see.
 */
export const Route = createFileRoute('/onboarding/first-meeting')({
  component: OnboardingFirstMeetingPage,
});

/** The one conversation on screen, as its fields hold it. */
interface Planned {
  title: string;
  /** Who it is with, by name. Empty is an answer: naming nobody is allowed. */
  attendees: string[];
  day: string;
  hour: string;
  /**
   * Empty until the user picks one. It stays empty for an organisation with a
   * single gesprekstype: that one is filled in when the form is read, so the
   * screen keeps its other fields.
   */
  templateId: string;
}

/** Already carrying a moment: only the title is ever missing at the start. */
function blank(): Planned {
  const start = defaultStart();

  return {
    title: '',
    attendees: [],
    day: dayValue(start),
    hour: hourValue(start),
    templateId: '',
  };
}

/** A conversation that is ready to be written down: it names one, and a type. */
interface ReadyMeeting extends PlannedMeeting {
  templateId: string;
  attendees: string[];
}

/**
 * The conversation as the preview and the API want it, or null while it is not
 * ready yet.
 *
 * A missing gesprekstype is what holds the step up when there is more than one:
 * a meeting only the user knows about has no guests and no history to infer one
 * from. With exactly one, `defaultMeetingTemplateId` answers it and nothing is
 * ever held back for a question that was not asked.
 */
function readyMeeting(
  planned: Planned,
  templates: MeetingTemplateItem[],
): ReadyMeeting | null {
  const startsAt = momentOf(planned.day, planned.hour);
  const templateId = planned.templateId || defaultMeetingTemplateId(templates);
  if (planned.title.trim() === '' || startsAt === null || templateId === '') {
    return null;
  }

  return {
    id: 'first',
    title: planned.title.trim(),
    startsAt,
    templateId,
    attendees: planned.attendees,
  };
}

export function OnboardingFirstMeetingPage() {
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const [planned, setPlanned] = useState<Planned>(blank);
  const peopleId = useId();

  const { data: user } = useQuery({
    queryKey: ['currentUser'],
    queryFn: fetchCurrentUser,
  });

  const { data: templates = [] } = useQuery({
    queryKey: ['meetingTemplates', user?.id, 'use'],
    queryFn: () => fetchMeetingTemplates(user!.id, 'use'),
    enabled: Boolean(user?.id),
  });

  /*
   * With one gesprekstype the form stays a title, a day and an hour, and that
   * one type is applied silently. From two onwards the dropdown appears,
   * because nothing about a meeting typed in by hand tells Wundo which
   * structure it should follow.
   */
  const asksForType = meetingTypeIsAChoice(templates);

  const ready = readyMeeting(planned, templates);

  /*
   * The way out is the conversation itself, where its preparation is written.
   * A failure keeps the form: what the user typed is still on screen and the
   * only thing missing is a second attempt.
   */
  const create = useMutation({
    mutationFn: (meeting: ReadyMeeting) =>
      createManualMeeting(user!.id, {
        title: meeting.title,
        starts_at: meeting.startsAt,
        template_id: meeting.templateId,
        attendees: meeting.attendees.map((name) => ({ name })),
      }),
    onSuccess: async (created) => {
      await queryClient.invalidateQueries({ queryKey: ['meetings'] });
      void navigate({
        to: '/meetings/$meetingId',
        params: { meetingId: created.id },
      });
    },
  });

  const change = (patch: Partial<Planned>) => {
    setPlanned((current) => ({ ...current, ...patch }));
  };

  return (
    <Card
      padding='none'
      className='mx-auto flex w-full max-w-225 overflow-hidden max-agenda:flex-col'
    >
      <div className='min-w-0 flex-1 px-9 py-10'>
        <p className='mb-3.5 text-xs font-bold tracking-wider text-ink-muted uppercase'>
          {m.onboarding_first_meeting_kicker()}
        </p>
        <h1 className='mb-2 text-xl font-semibold tracking-tight'>
          {m.onboarding_first_meeting_title()}
        </h1>
        <p className='mb-6 text-md text-ink-muted'>
          {m.onboarding_first_meeting_subtitle()}
        </p>

        {create.isError && (
          <p
            role='alert'
            className='mb-5 rounded-lg border border-danger-line bg-danger-fill px-4 py-3 text-sm font-medium text-danger'
          >
            {m.onboarding_first_meeting_failed()}
          </p>
        )}

        {/*
          A date and a time field are as wide as the browser draws them — around
          9.5rem and 8rem with their picker icons — and the form sits in half a
          card. Title, day and hour side by side would leave the title a stub, so
          the title owns a line of its own and the moment sits under it.
        */}
        <div className='flex flex-col gap-2.5'>
          <label className='flex min-w-0 flex-col gap-1.5'>
            <span className='text-sm font-semibold text-ink'>
              {m.onboarding_first_meeting_title_label()}
            </span>
            <Input
              aria-label={m.onboarding_first_meeting_title_label()}
              placeholder={m.onboarding_first_meeting_title_placeholder()}
              value={planned.title}
              onChange={(event) => change({ title: event.target.value })}
            />
          </label>
          {/*
            Right under the title, because it is the same question continued:
            what the conversation is, and who it is with. Nothing is offered to
            pick from — somebody in the wizard has no colleagues Wundo knows of
            yet, and a name typed here for the first time is the whole point.
          */}
          <div className='flex min-w-0 flex-col gap-1.5'>
            <label htmlFor={peopleId} className='text-sm font-semibold text-ink'>
              {m.manual_meeting_with_label()}{' '}
              <span className='font-normal text-ink-muted'>
                {m.manual_meeting_with_optional()}
              </span>
            </label>
            <Combobox
              id={peopleId}
              values={planned.attendees}
              onValuesChange={(attendees) => change({ attendees })}
              suggestions={[]}
              placeholder={m.manual_meeting_with_placeholder()}
              removeLabel={(person) => m.manual_meeting_with_remove({ name: person })}
            />
            <p className='text-xs text-ink-muted'>{m.manual_meeting_with_hint()}</p>
          </div>
          <div className='flex flex-wrap items-end gap-2.5'>
            <label className='flex min-w-40 flex-1 flex-col gap-1.5'>
              <span className='text-sm font-semibold text-ink'>
                {m.manual_meeting_day_label()}
              </span>
              <Input
                type='date'
                aria-label={m.manual_meeting_day_label()}
                value={planned.day}
                onChange={(event) => change({ day: event.target.value })}
              />
            </label>
            <label className='flex min-w-32 flex-1 flex-col gap-1.5'>
              <span className='text-sm font-semibold text-ink'>
                {m.manual_meeting_hour_label()}
              </span>
              <Input
                type='time'
                aria-label={m.manual_meeting_hour_label()}
                value={planned.hour}
                onChange={(event) => change({ hour: event.target.value })}
              />
            </label>
          </div>
          {/*
            Only when there is something to choose between. With a single
            gesprekstype the form keeps its three fields and that type is
            applied without ever being mentioned.
          */}
          {asksForType && (
            <label className='flex min-w-0 flex-col gap-1.5'>
              <span className='text-sm font-semibold text-ink'>
                {m.manual_meeting_type_label()}
              </span>
              <Select
                aria-label={m.manual_meeting_type_label()}
                wrapperClassName='w-full'
                className='w-full'
                value={planned.templateId}
                options={meetingTypeOptions(templates, planned.templateId)}
                onChange={(event) => change({ templateId: event.target.value })}
              />
            </label>
          )}
        </div>

        <div className='mt-7 flex items-center gap-3'>
          <Button
            variant='accent'
            shape='block'
            size='lg'
            disabled={user === undefined || create.isPending || ready === null}
            onClick={() => ready !== null && create.mutate(ready)}
          >
            {m.onboarding_first_meeting_continue()}
            <ArrowRight aria-hidden='true' className='size-4' />
          </Button>
          <Button
            variant='ghost'
            shape='block'
            size='lg'
            /*
             * Back to the step before this one on whichever branch this is. An
             * organisation without the calendar integration never saw the ask,
             * and sending them there would only bounce them straight back.
             */
            onClick={() =>
              void navigate({
                to:
                  user?.calendar_enabled === false
                    ? '/onboarding/what-you-get'
                    : '/onboarding/calendar',
              })
            }
          >
            {m.onboarding_back()}
          </Button>
        </div>
      </div>

      <div className='flex min-w-0 flex-1 items-center justify-center border-l border-fill bg-fill-faint p-7.5 max-agenda:border-t max-agenda:border-l-0'>
        <PlannedMeetingsPreview meetings={ready === null ? [] : [ready]} />
      </div>
    </Card>
  );
}
